import { useCommunityGoal } from '@/hooks/useCommunityGoal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Target, Trophy, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CommunityGoalWidgetProps {
  className?: string;
}

export const CommunityGoalWidget = ({ className }: CommunityGoalWidgetProps) => {
  const { goal, isLoading } = useCommunityGoal();

  if (isLoading) {
    return (
      <Card className={cn("", className)}>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!goal) {
    return null;
  }

  const progress = Math.min((goal.current_count / goal.target_count) * 100, 100);
  const remaining = Math.max(goal.target_count - goal.current_count, 0);

  return (
    <Card className={cn(
      "overflow-hidden transition-all",
      goal.is_completed && "ring-2 ring-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.3)]",
      className
    )}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          {goal.is_completed ? (
            <Trophy className="h-5 w-5 text-amber-500" />
          ) : (
            <Target className="h-5 w-5 text-primary" />
          )}
          Community Goal
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Goal Description */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-1">This week's challenge:</p>
          <p className="font-medium">
            Give {goal.target_count} {goal.reaction_emoji} {goal.reaction_label} reactions
          </p>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <Progress 
            value={progress} 
            className={cn(
              "h-3",
              goal.is_completed && "[&>div]:bg-gradient-to-r [&>div]:from-amber-400 [&>div]:to-yellow-500"
            )} 
          />
          <div className="flex justify-between text-sm">
            <span className="font-semibold">
              {goal.current_count}/{goal.target_count}
            </span>
            {goal.is_completed ? (
              <span className="text-amber-600 font-semibold flex items-center gap-1">
                🎉 Goal Reached!
              </span>
            ) : (
              <span className="text-muted-foreground">
                {remaining} more to go!
              </span>
            )}
          </div>
        </div>

        {/* Top Contributors */}
        {goal.top_contributors.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">Top Contributors</p>
            <div className="space-y-2">
              {goal.top_contributors.map((contributor, index) => (
                <div 
                  key={contributor.profile_id}
                  className="flex items-center gap-2 text-sm"
                >
                  <span className="w-4 text-muted-foreground font-medium">
                    {index + 1}.
                  </span>
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={contributor.avatar_url || undefined} />
                    <AvatarFallback className="text-xs">
                      {contributor.name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="flex-1 truncate">{contributor.name}</span>
                  <span className="text-muted-foreground font-medium">
                    {contributor.count} {goal.reaction_emoji}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CommunityGoalWidget;
